/* eslint-disable no-empty-pattern */
import * as Sentry from '@sentry/browser';
import Compressor from 'compressorjs';
import { v4 as UUID } from 'uuid';
import { FBAuth, FBModule, FBStorageRef } from '@/helpers/firebaseServices';

// Profile photos live in Storage under the user and are exposed as the Firebase Auth photoURL,
// which is what the iOS app shows as the profile picture
const profilePhotoFolder = (uid) => `users/${uid}/profile`;

function compressProfilePhoto(file) {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line no-new
    new Compressor(file, {
      quality: 0.9, maxWidth: 512, maxHeight: 512, mimeType: 'image/jpeg', success: resolve, error: reject,
    });
  });
}

export default {
  namespaced: true,
  state: {
    isInitialLoading: true,
    authData: {
      id: null,
      creationTime: null,
      lastSignInTime: null,
      displayName: '',
      email: '',
      guestPhotoUrl: '',
      emailVerified: false,
      providers: [],
    },
    signInPopupIsOpen: false,
  },
  getters: {
    authDataId: (state) => state.authData.id,
  },
  mutations: {
    RESET_STATE(state) {
      state.isInitialLoading = true;
      state.authData = {
        id: null,
        creationTime: null,
        lastSignInTime: null,
        displayName: '',
        email: '',
        guestPhotoUrl: '',
        emailVerified: false,
        providers: [],
      };
      state.signInPopupIsOpen = false;

      Sentry.configureScope((scope) => scope.clear());
    },
    SET_IS_INITIAL_LOADING(state, isInitialLoading) {
      state.isInitialLoading = isInitialLoading;
    },
    SET_AUTH_DATA(state, authData) {
      state.authData = {
        // id: authData.id || state.authData.id, // TODO: Replace all id/uid
        id: authData.uid || authData.id || state.authData.id,
        creationTime: authData.creationTime || state.authData.creationTime,
        lastSignInTime: authData.lastSignInTime || state.authData.lastSignInTime,
        displayName: authData.displayName || state.authData.displayName,
        email: authData.email || state.authData.email,
        guestPhotoUrl: authData.photoURL || authData.guestPhotoUrl || state.authData.guestPhotoUrl,
        emailVerified: authData.emailVerified || state.authData.emailVerified,
        providers: authData.providers || state.authData.providers,
      };

      Sentry.configureScope((scope) => {
        scope.setUser({
          id: state.authData.id,
          username: state.authData.displayName,
          email: state.authData.email,
          emailIsVerified: state.authData.emailVerified,
        });
      });
    },
    SET_PHOTO_URL(state, photoURL) {
      state.authData.guestPhotoUrl = photoURL || '';
    },
    SET_EMAIL_VERIFIED(state, emailVerified) {
      state.authData.emailVerified = Boolean(emailVerified);
    },
    SET_SIGN_IN_POPUP_IS_OPEN(state, signInPopupIsOpen) {
      state.signInPopupIsOpen = signInPopupIsOpen;
    },
  },
  actions: {
    resetState({ commit }) {
      commit('RESET_STATE');
    },
    updateIsInitialLoading({ commit }, isInitialLoading) {
      commit('SET_IS_INITIAL_LOADING', isInitialLoading);
    },
    async updateAuthData({ commit }, authData) {
      const signInProviders = authData.providerData.map((profile) => profile.providerId);
      const userAuthData = { ...authData, providers: signInProviders };

      commit('SET_AUTH_DATA', userAuthData);

      return Promise.resolve();
    },
    getAuthToken() {
      return FBAuth.currentUser.getIdToken()
        .then((token) => Promise.resolve(token))
        .catch((error) => Promise.reject(error));
    },
    signUserInWithEmailPassword({ commit }, { email, password }) {
      return FBAuth.signInWithEmailAndPassword(email, password)
        .then((result) => {
          commit('SET_AUTH_DATA', {
            id: result.user.uid,
            creationTime: result.user.metadata.creationTime,
            lastSignInTime: result.user.metadata.lastSignInTime,
            displayName: result.user.displayName,
            email: result.user.email,
            guestPhotoUrl: result.user.guestPhotoUrl,
            emailVerified: result.user.emailVerified,
          });

          const { dataLayer } = window;
          dataLayer.push({ event: 'user-signed-in-with-email-password' });

          return Promise.resolve(result.user);
        })
        .catch((error) => Promise.reject(error));
    },
    signUserUpWithEmailPassword({ commit, dispatch }, { email, password, username }) {
      return FBAuth.createUserWithEmailAndPassword(email, password)
        .then((result) => {
          commit('SET_AUTH_DATA', {
            id: result.user.uid,
            creationTime: result.user.metadata.creationTime,
            lastSignInTime: result.user.metadata.lastSignInTime,
            displayName: result.user.displayName,
            guestPhotoUrl: result.user.guestPhotoUrl,
            emailVerified: result.user.emailVerified,
            email,
          });
          dispatch('sendEmailVerification');
          dispatch('updateProfile', username);

          const { dataLayer } = window;
          dataLayer.push({ event: 'user-signed-up-with-email-password' });

          return Promise.resolve(result.user);
        })
        .catch((error) => Promise.reject(error));
    },
    updateProfile({ commit }, username) {
      return FBAuth.currentUser.updateProfile({
        displayName: username,
        // guestPhotoUrl: '', TODO: Set a profile picture
      })
        .then((response) => {
          commit('SET_AUTH_DATA', {
            displayName: username,
          });
          return Promise.resolve(response);
        })
        .catch((error) => Promise.reject(error));
    },
    linkWithCredential({ commit }, { email, password }) {
      const credential = FBModule.auth.EmailAuthProvider.credential(email, password);

      return FBAuth.currentUser.linkWithCredential(credential)
        .then((userCredential) => {
          const signInProviders = userCredential.user.providerData.map((profile) => profile.providerId);

          commit('SET_AUTH_DATA', {
            email: userCredential.user.email,
            providers: signInProviders,
          });

          return Promise.resolve(userCredential);
        })
        .catch((error) => Promise.reject(error));
    },
    reauthenticateWithCredential({}, { email, password }) {
      const credential = FBModule.auth.EmailAuthProvider.credential(email, password);

      return FBAuth.currentUser.reauthenticateWithCredential(credential)
        .then(() => Promise.resolve())
        .catch((error) => Promise.reject(error));
    },
    updateEmail({ commit }, email) {
      return FBAuth.currentUser.updateEmail(email)
        .then(() => {
          commit('SET_AUTH_DATA', {
            email,
          });

          return Promise.resolve(email);
        })
        .catch((error) => Promise.reject(error));
    },
    updatePassword({}, password) {
      return FBAuth.currentUser.updatePassword(password)
        .then(() => Promise.resolve())
        .catch((error) => Promise.reject(error));
    },
    async uploadProfilePhoto({ commit, state }, file) {
      const user = FBAuth.currentUser;
      if (!user) throw new Error('You are not signed in.');

      let upload = file;
      try {
        upload = await compressProfilePhoto(file);
      } catch (error) {
        console.error(error);
      }

      const previousPhotoURL = user.photoURL;
      const ref = FBStorageRef.child(`${profilePhotoFolder(user.uid)}/photo-${UUID()}.jpeg`);
      await ref.put(upload, { contentType: 'image/jpeg' });
      const photoURL = await ref.getDownloadURL();
      await user.updateProfile({ photoURL });
      commit('SET_PHOTO_URL', photoURL);

      // Remove the previous photo when it was uploaded here (not a Google/Facebook avatar)
      if (previousPhotoURL && previousPhotoURL.includes(encodeURIComponent(profilePhotoFolder(user.uid)))) {
        FBModule.storage().refFromURL(previousPhotoURL).delete().catch((error) => console.error(error));
      }
      return state.authData.guestPhotoUrl;
    },
    async removeProfilePhoto({ commit }) {
      const user = FBAuth.currentUser;
      if (!user) throw new Error('You are not signed in.');

      const previousPhotoURL = user.photoURL;
      await user.updateProfile({ photoURL: null });
      commit('SET_PHOTO_URL', '');

      if (previousPhotoURL && previousPhotoURL.includes(encodeURIComponent(profilePhotoFolder(user.uid)))) {
        FBModule.storage().refFromURL(previousPhotoURL).delete().catch((error) => console.error(error));
      }
    },
    // Re-reads the account so a verification done in another tab/device shows up
    async refreshEmailVerified({ commit }) {
      const user = FBAuth.currentUser;
      if (!user) return false;

      await user.reload();
      commit('SET_EMAIL_VERIFIED', FBAuth.currentUser.emailVerified);
      return FBAuth.currentUser.emailVerified;
    },
    sendEmailVerification() {
      return FBAuth.currentUser.sendEmailVerification()
        .then((response) => Promise.resolve(response))
        .catch((error) => Promise.reject(error));
    },
    sendPasswordResetEmail({}, { email }) {
      return FBAuth.sendPasswordResetEmail(email)
        .then((response) => Promise.resolve(response))
        .catch((error) => Promise.reject(error));
    },
    signUserInWithGoogle({ commit }) {
      commit('SET_SIGN_IN_POPUP_IS_OPEN', true);
      return FBAuth.signInWithPopup(new FBModule.auth.GoogleAuthProvider())
        .then((result) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          commit('SET_AUTH_DATA', {
            id: result.user.uid,
            creationTime: result.user.metadata.creationTime,
            lastSignInTime: result.user.metadata.lastSignInTime,
            displayName: result.user.displayName,
            email: result.user.email,
            guestPhotoUrl: result.user.guestPhotoUrl,
            emailVerified: result.user.emailVerified,
          });

          const { dataLayer } = window;
          dataLayer.push({ event: 'user-signed-in-with-google' });

          return Promise.resolve(result);
        })
        .catch((error) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          return Promise.reject(error);
        });
    },
    signUserInWithFacebook({ commit }) {
      commit('SET_SIGN_IN_POPUP_IS_OPEN', true);
      return FBAuth.signInWithPopup(new FBModule.auth.FacebookAuthProvider())
        .then((result) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          commit('SET_AUTH_DATA', {
            id: result.user.uid,
            creationTime: result.user.metadata.creationTime,
            lastSignInTime: result.user.metadata.lastSignInTime,
            displayName: result.user.displayName,
            email: result.user.email,
            guestPhotoUrl: result.user.guestPhotoUrl,
            emailVerified: result.user.emailVerified,
          });

          const { dataLayer } = window;
          dataLayer.push({ event: 'user-signed-in-with-facebook' });

          return Promise.resolve(result);
        }).catch((error) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          return Promise.reject(error);
        });
    },
    // Accounts created with Sign in with Apple on iOS use the same Apple user id,
    // so this signs them into their existing Eventor account.
    signUserInWithApple({ commit }) {
      const provider = new FBModule.auth.OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      commit('SET_SIGN_IN_POPUP_IS_OPEN', true);
      return FBAuth.signInWithPopup(provider)
        .then((result) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          commit('SET_AUTH_DATA', {
            id: result.user.uid,
            creationTime: result.user.metadata.creationTime,
            lastSignInTime: result.user.metadata.lastSignInTime,
            displayName: result.user.displayName,
            email: result.user.email,
            guestPhotoUrl: result.user.guestPhotoUrl,
            emailVerified: result.user.emailVerified,
          });

          const { dataLayer } = window;
          if (dataLayer) dataLayer.push({ event: 'user-signed-in-with-apple' });

          return Promise.resolve(result);
        }).catch((error) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          return Promise.reject(error);
        });
    },
    linkAccountWithProvider({ commit }, providerId) {
      let provider = null;

      commit('SET_SIGN_IN_POPUP_IS_OPEN', true);

      if (providerId === 'google.com') provider = new FBModule.auth.GoogleAuthProvider();
      else if (providerId === 'facebook.com') provider = new FBModule.auth.FacebookAuthProvider();
      else if (providerId === 'apple.com') {
        provider = new FBModule.auth.OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
      }

      return FBAuth.currentUser.linkWithPopup(provider)
        .then((result) => {
          const signInProviders = result.user.providerData.map((profile) => profile.providerId);

          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);
          commit('SET_AUTH_DATA', {
            id: result.user.uid,
            creationTime: result.user.metadata.creationTime,
            lastSignInTime: result.user.metadata.lastSignInTime,
            displayName: result.user.displayName,
            email: result.user.email,
            guestPhotoUrl: result.user.guestPhotoUrl,
            emailVerified: result.user.emailVerified,
            providers: signInProviders,
          });
        })
        .catch((error) => {
          commit('SET_SIGN_IN_POPUP_IS_OPEN', false);

          return Promise.reject(error);
        });
    },
    unlinkAccountWithProvider({ commit }, providerId) {
      return FBAuth.currentUser.unlink(providerId)
        .then((result) => {
          const signInProviders = result.providerData.map((profile) => profile.providerId);

          commit('SET_AUTH_DATA', {
            providers: signInProviders,
          });
        })
        .catch((error) => Promise.reject(error));
    },
    handleResetPasswordValidityCheck({}, { actionCode }) {
      // Verify the validity of the password reset code.
      return FBAuth.verifyPasswordResetCode(actionCode)
        .then((email) => Promise.resolve(email))
        // Invalid or expired action code. Try to reset the password again.
        .catch((error) => Promise.reject(error));
    },
    handleResetPasswordAction({}, { actionCode, password }) {
      // TODO: If a continue URL is available, display a button which on click redirects the
      // user back to the app via continueUrl with
      // additional state determined from that URL's parameters.
      return FBAuth.confirmPasswordReset(actionCode, password)
        .then((response) => Promise.resolve(response))
        // Error encountered while sending password reset code.
        .catch((error) => Promise.reject(error));
    },
    handleVerifyEmail({ commit }, { actionCode }) {
      return FBAuth.applyActionCode(actionCode).then((response) => {
        commit('SET_AUTH_DATA', {
          emailVerified: true,
        });
        return Promise.resolve(response);
      }).catch((error) => Promise.reject(error));
    },
    signOut({ dispatch }) {
      return Promise.all([
        dispatch('packages/resetState', null, { root: true }),
        dispatch('resetState'),
        dispatch('user/events/resetState', null, { root: true }),
        dispatch('user/resetState', null, { root: true }),
        dispatch('user/package/resetState', null, { root: true }),
        dispatch('user/payment/resetState', null, { root: true }),
      ]).then(() => FBAuth.signOut()
        .then((response) => Promise.resolve(response))
        .catch((error) => Promise.reject(error)))
        .catch((error) => Promise.reject(error));
    },
  },
};
